import "./MissionBillingForm.scss";
import InfoContext from "pages/InfoContext";
import InputSearchTag from "core/inputs/input-search/InputSearchTag";
import { Controller } from "utils/Controller";
import { FormattedMessage, useIntl } from "react-intl";
import { FILTERS_FOR_NAMES, MODAL_CONTENT, TAG_TYPES, TARIFICATION_TYPE } from "constants/constants";
import Icon from "core/icon/Icon";
import { Typography } from "core/typography/Typography";
import { useContext, useEffect, useState } from "react";
import {
  FORM_INFO,
  MISSION_FORM_LABELS,
  MISSION_FORM_PLACEHOLDERS,
} from "constants/forms";
import {
  formatNames,
  renderInputCurrency,
  renderInputBillingType,
  renderInputADV,
  renderInputHonorary,
  renderInputMissionDates,
  renderInputMissionBillingCondition,
  formatUsersOptions,
} from "../CommonInputs";
import propTypes from "prop-types";
import { ROLE_ADV, ROLE_RADV } from "access-roles/roles";
import { useGeneralContext } from "api/directory/general/GeneralProvider";

const MissionBillingForm = ({
  readOnly = false,
  control,
  register,
  setValue,
  watch,
  errors,
  missionBySlug,
  missionCurrentFee,
}) => {
  const {
    branches,
    lang,
    currencyList,
    id,
    currencyType,
    setCurrencyType,
    cellHighlight,
    setOpenModalIntent,
  } = useContext(InfoContext);
  const { getNamesByFilters } = useGeneralContext();
  const charged_by_id = watch("charged_by_id", []);
  const intl = useIntl();
  const currency_id = watch("currency_id", "");
  const billing_type = watch("billing_type", "");
  const adv_id = watch("adv_id", "");
  const mission_fee = watch("mission_fee", "");
  const [advSearchResults, setAdvSearchResults] = useState([]);

  useEffect(() => {
    const { currency_symbol } =
      currencyList?.find((elem) => elem.id === currency_id) || {};
    const newCurrencyType = currency_symbol || "€";
    setCurrencyType(newCurrencyType);
  }, [currency_id, currencyList]);

  const renderInputChargedById = () => (
    <Controller
      {...{
        control,
        register,
        name: "charged_by_id",
        rules: {
          required: true,
        },
        render: () => (
          <InputSearchTag
            label={MISSION_FORM_LABELS.charged_by_id}
            limitChoice={1}
            onDelete={(val) =>
              setValue(
                "charged_by_id",
                charged_by_id.filter((elem) => elem.value !== val)
              )
            }
            options={formatNames(branches)}
            placeholder={MISSION_FORM_PLACEHOLDERS.charged_by_id}
            popup={FORM_INFO.branch.popup}
            readOnly={readOnly}
            required
            value={charged_by_id}
            selectedOptions={charged_by_id}
            setValue={(val) => setValue("charged_by_id", val)}
            type={TAG_TYPES.BRANCHES}
            errors={errors["charged_by_id"]}
            id="assigned-charged_by_id"
          />
        ),
      }}
    />
  );

  return (
    <div className="section-billing">
      <Typography
        className="title-3 violet text-uppercase"
        type="h2"
        label={<FormattedMessage id="billing" defaultMessage="FACTURATION" />}
      />
      <div className="block">
        <div className="left">
          <div className="line-1">
            <div
              className={`cell-padding ${!charged_by_id.length && cellHighlight && "errorCellColor"
                }`}
            >
              {renderInputChargedById()}
            </div>
            <div
              className={`cell-padding ${!currency_id && cellHighlight && "errorCellColor"
                }`}
            >
              {" "}
              {renderInputCurrency({
                control,
                register,
                errors,
                errorValue: null,
                errorMessage: errors?.["currency_id"]?.message || null,
                readOnly,
                value: currency_id,
                currencyList: currencyList,
                setValue,
                label: MISSION_FORM_LABELS.currency_id,
                placeholder: MISSION_FORM_PLACEHOLDERS.currency_id,
              })}
            </div>
            <div
              className={`cell-padding ${!billing_type && cellHighlight && "errorCellColor"
                }`}
            >
              {" "}
              {renderInputBillingType({
                control,
                register,
                readOnly,
                errors,
                errorValue: null,
                errorMessage: errors?.["billing_type"]?.message || null,
                setValue,
                value: billing_type,
                id,
                label: MISSION_FORM_LABELS.billing_type,
                placeholder: MISSION_FORM_LABELS.billing_type,
              })}
            </div>
          </div>

          <div className="line-2">
            <div
              className={`cell-padding ${missionBySlug && !adv_id?.length && cellHighlight && "errorCellColor"
                }`}
            >
              {renderInputADV({
                control,
                register,
                readOnly,
                required: !!missionBySlug,
                getOptions: async (search) => {
                  const results = await getNamesByFilters({
                    role: [ROLE_ADV, ROLE_RADV],
                    types: [FILTERS_FOR_NAMES.PERSON],
                    search,
                  });
                  setAdvSearchResults(results);
                  return results;
                },
                options: formatUsersOptions({
                  users: advSearchResults,
                  type: TAG_TYPES.PEOPLE,
                }),
                setValue,
                value: adv_id,
                errors,
                label: MISSION_FORM_LABELS.adv_id,
                placeholder: MISSION_FORM_PLACEHOLDERS.adv_id,
              })}
            </div>
            <div
              className={`cell-padding ${!readOnly && missionBySlug ? "cell-disabled" : ""
                }  ${!mission_fee && billing_type !== TARIFICATION_TYPE.OTHER && cellHighlight && "errorCellColor"}`}
              style={{ position: "relative" }}
            >
              {!readOnly && missionBySlug && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (setOpenModalIntent) {
                      setOpenModalIntent(MODAL_CONTENT.MANAGE_DAILY_FEE);
                    }
                  }}
                  data-testid="mission_fee-edit-honorary-icon"
                  style={{
                    position: "absolute",
                    top: 6,
                    right: 6,
                    zIndex: 1,
                    background: "none",
                    border: "none",
                    padding: 4,
                    cursor: "pointer",
                    pointerEvents: "auto",
                  }}
                >
                  <Icon
                    icon="pencil"
                    color="violet"
                    width={18}
                    height={18}
                    onClick={() => {}}
                  />
                </button>
              )}
              {renderInputHonorary({
                control,
                register,
                errorValue: null,
                errorMessage: errors?.["mission_fee"]?.message || null,
                required: billing_type !== TARIFICATION_TYPE.OTHER,
                errors,
                value: missionCurrentFee,
                name: "mission_fee",
                label: `${intl.formatMessage({
                  id: MISSION_FORM_LABELS.mission_fee.id,
                  defaultMessage:
                    MISSION_FORM_LABELS.mission_fee.defaultMessage,
                })} (${currencyType})`,
                placeholder: MISSION_FORM_PLACEHOLDERS.mission_fee,
                readOnly: !missionBySlug ? readOnly : true,
              })}
            </div>
            <div className="cell-padding">
              {renderInputMissionDates({
                control,
                register,
                readOnly,
                lang,
                name: "scheduled_end_date",
                required: false,
                errors,
                id: MISSION_FORM_LABELS.scheduled_end_date,
                label: MISSION_FORM_LABELS.scheduled_end_date,
                placeholder: MISSION_FORM_LABELS.scheduled_end_date,
                setValue,
                value: watch("scheduled_end_date") || null,
              })}
            </div>
          </div>
        </div>
        <div className="right">
          <div className="cell-padding">
            {renderInputMissionBillingCondition({
              control,
              register,
              readOnly,
              errors,
              name: "billing_condition",
              id: MISSION_FORM_LABELS.billing_condition,
              width: "100%",
              label: MISSION_FORM_LABELS.billing_condition,
              placeholder: MISSION_FORM_PLACEHOLDERS.billing_condition,
            })}
          </div>
          {billing_type === "daily" ? (
            <div className="cell-padding">
              {renderInputMissionBillingCondition({
                control,
                register,
                readOnly,
                errors,
                name: "billing_daily_fee_comment",
                id: MISSION_FORM_LABELS.billing_daily_fee_comment,
                label: MISSION_FORM_LABELS.billing_daily_fee_comment,
                placeholder:
                  MISSION_FORM_PLACEHOLDERS.billing_daily_fee_comment,
                width: "100%",
              })}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default MissionBillingForm;

MissionBillingForm.propTypes = {
  control: propTypes.object,
  errors: propTypes.object,
  missionBySlug: propTypes.object,
  missionCurrentFee: propTypes.number,
  readOnly: propTypes.bool,
  register: propTypes.func,
  setValue: propTypes.func,
  watch: propTypes.func,
};
